import { useNavigate } from "react-router-dom";
import { UserX, LogOut, LogIn } from "lucide-react";

import { AuthLayout } from "@/components/ui/auth-layout";
import { Panel } from "@/components/ui/panel";
import { Heading } from "@/components/ui/heading";
import { Text, Muted } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { backendAuth } from "@/lib/auth/backend-auth";

interface NoAccessIdentity {
  displayName: string;
  email: string;
  userID: string;
  loadError: string;
}

export function resolveNoAccessIdentity(params: {
  userId: string | null;
  name?: string;
  email?: string;
  errorMessage?: string;
}): NoAccessIdentity {
  const userID = params.userId || "";
  const displayName =
    params.name?.trim() || params.email || userID || "Account details unavailable";
  const email = params.email || "";
  const loadError = params.errorMessage || "";

  return {
    displayName,
    email,
    userID,
    loadError,
  };
}

export default function NoAccessRoute() {
  const navigate = useNavigate();
  const { me, userId, error, isLoading } = useAuth();
  const identity = resolveNoAccessIdentity({
    userId,
    name: me?.name,
    email: me?.email,
    errorMessage: error?.message,
  });
  const isIdentityLoading = isLoading;

  return (
    <AuthLayout>
      <Panel padding="lg" className="space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-amber-100 p-3">
            <UserX className="h-10 w-10 text-amber-700" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <Heading size="lg">No Company Access</Heading>
          <Muted>
            You are signed in but do not belong to any company. Contact your administrator or use an
            invite link.
          </Muted>
        </div>

        <Text className="text-center">
          Once your company membership is created, sign in again to continue.
        </Text>

        <Panel className="space-y-1 bg-muted/30">
          <Text tone="strong">{identity.displayName}</Text>
          {isIdentityLoading ? (
            <Muted>Loading account details...</Muted>
          ) : (
            <>
              {identity.email !== "" && <Muted>{identity.email}</Muted>}
              {identity.userID !== "" && <Muted>User ID: {identity.userID}</Muted>}
              {identity.email === "" && identity.userID === "" && (
                <Muted>
                  {identity.loadError !== ""
                    ? `Unable to load account details: ${identity.loadError}`
                    : "Unable to load account details right now."}
                </Muted>
              )}
            </>
          )}
        </Panel>

        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            className="w-full"
            onClick={() => {
              void backendAuth.logout({ returnTo: "/login" });
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
          <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
            <LogIn className="mr-2 h-4 w-4" />
            Go to Login
          </Button>
        </div>
      </Panel>
    </AuthLayout>
  );
}
